import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  PublicUserInviteQuery,
  useAcceptPublicUserInviteMutation,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { colors, spacing } from "../../../stitches.config";
import { validatePasswordWithTranslations } from "../../../utils/passwords";
import { Container as LoginContainer } from "../../Authentication/CommonLoginComponents/__styles__/FormWithFullPageBackground";
import { CenterContent } from "../../Common/__styles__/Layout";
import { Button } from "../../Common/Button";
import { Section, Sections } from "../../Common/Layout";
import { TermsOfUseInput } from "../../Common/TermsOfUseInput";
import { Body, Title } from "../../Common/Typography";
import { Text } from "../../Inputs/react-hook-form";
import Layout from "../Layout";
import { INVITE_FORM_MAX_WIDTH } from "./constants";

type PublicUserInviteProps = {
  invite: NonNullable<PublicUserInviteQuery["publicUserInvite"]>;
  redirect: Maybe<string>;
};

interface FormStructure {
  firstName: string;
  lastName: string;
  password: string;
  terms: boolean;
}

export const PublicUserInvite = ({
  invite,
  redirect,
}: PublicUserInviteProps) => {
  const { addErrorToast } = useStatusToasts();
  const { t } = useTranslation();

  const [acceptInvite, { loading: mutationLoading }] =
    useAcceptPublicUserInviteMutation({
      onCompleted: data => {
        if (data.acceptPublicUserInvite.ok) {
          window.location.href = redirect || "/";
          return;
        }

        addErrorToast(
          data.acceptPublicUserInvite.error?.message ??
            "There was an error accepting this invite. Please try again.",
        );
      },
      onError: () => {
        addErrorToast(
          "There was an error accepting this invite. Please try again. If the problem persists, please email us at support@withforerunner.com",
        );
      },
    });

  const useFormProps = useForm<FormStructure>({
    defaultValues: {
      firstName: "",
      lastName: "",
      password: "",
      terms: false,
    },
  });
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useFormProps;

  const onSubmit = handleSubmit(async data => {
    await acceptInvite({
      variables: {
        firstName: data.firstName,
        lastName: data.lastName,
        inviteId: invite.id,
        password: data.password,
      },
    });
  });

  return (
    <Layout
      backgroundColor={colors.bgUiContainerInverted}
      footerFontColor="contentPrimaryInverted"
    >
      <CenterContent style={{ padding: spacing.l.value, flex: 1 }}>
        <LoginContainer style={{ maxWidth: INVITE_FORM_MAX_WIDTH }}>
          <FormProvider {...useFormProps}>
            <Sections gap={spacing.m}>
              <Title size="large" type="semiBold">
                Create your account
              </Title>
              <Section>
                <Body
                  size="large"
                  type="emphasis"
                  color="contentPrimary"
                  style={{
                    backgroundColor: colors.bgUiContainer.value,
                    padding: spacing.m.value,
                  }}
                >
                  {invite.email}
                </Body>
              </Section>
              {invite.userGroup && (
                <Section>
                  <Body size="default" type="regular" color="contentSecondary">
                    Role: {invite.userGroup.name}
                  </Body>
                </Section>
              )}
              <Section flexDirection="row">
                <Text
                  label="First Name"
                  error={errors.firstName?.message}
                  required
                  {...register("firstName", {
                    required: "Required field",
                  })}
                />
                <Text
                  label="Last Name"
                  error={errors.lastName?.message}
                  required
                  {...register("lastName", {
                    required: "Required field",
                  })}
                />
              </Section>
              <Section gap={spacing.s}>
                <Text
                  label="Password"
                  type="password"
                  error={errors.password?.message}
                  placeholder="********"
                  required
                  {...register("password", {
                    validate: (val: string) => {
                      return validatePasswordWithTranslations({
                        password: val,
                        minimumPasswordLength:
                          invite.account.securityConfiguration
                            .minimumPasswordLength,
                        t,
                      });
                    },
                  })}
                />
                <Body size="small" type="regular" color="contentSecondary">
                  Create a password with at least{" "}
                  {invite.account.securityConfiguration.minimumPasswordLength}{" "}
                  characters. Include at least one number, one upper case
                  letter, and one special character.
                </Body>
              </Section>
              <Section>
                <TermsOfUseInput control={control} errors={errors} />
                <Button
                  style={{
                    justifyContent: "center",
                    marginTop: spacing.m.value,
                  }}
                  type="submit"
                  styleVariant="primary"
                  size="medium"
                  onClick={onSubmit}
                  disabled={mutationLoading}
                >
                  Create account
                </Button>
              </Section>
            </Sections>
          </FormProvider>
        </LoginContainer>
      </CenterContent>
    </Layout>
  );
};
